import React, { FC } from "react";
import { useParams } from "react-router-dom";
import { NotificationBanners } from "../../../common/components/NotificationBanners/notification-banners";
import { CancelOrderButton } from "../../../common/components/OrderCancellation/cancel-order-button";
import { safeDecodeURIComponent } from "../../../common/helpers/string.util";
import { Paths } from "../../../common/paths/paths";
import { getString } from "../../../common/uistringlabels/ui-string-utils";
import { toAmznhamTrackingView } from "./amznham-tracking-view.mapper";
import { useAmznhamOrder } from "./hooks/use-amznham-order.hook";
import { useCatalogItemImage } from "./hooks/use-catalog-item-image.hook";
import { useCancelAmznhamOrder } from "./hooks/use-cancel-amznham-order.hook";
import { OrderSupportActions } from "./OrderSupport/order-support-actions";
import { OrderTrackingLayout } from "../components/OrderTrackingLayout/order-tracking-layout";
import { extractOrderDetails } from "../../../services/orders/cancellation.types";
import type { Order } from "../../../services/orders/orders.types";

interface CancelAmznhamOrderProps {
  order: Order;
}

const CancelAmznhamOrder: FC<CancelAmznhamOrderProps> = ({ order }) => {
  const { cancelOrder, isCancelling } = useCancelAmznhamOrder(order.orderId);

  return (
    <CancelOrderButton
      orderStatus={order.status}
      orderId={order.orderId}
      orderDetails={extractOrderDetails(order)}
      cancellationState={{ isLoading: isCancelling, error: null }}
      onConfirmCancellation={cancelOrder}
      orderItems={order.items}
      isFullWidth
    />
  );
};

const AmznhamOrderTrackingPage: FC = () => {
  const { orderId: encodedOrderId } = useParams<{ orderId?: string }>();
  // ITO order IDs hold slashes, so links encode them and the route keeps them encoded.
  const orderId = encodedOrderId && safeDecodeURIComponent(encodedOrderId);
  const { data: order, isLoading, isError, refetch } = useAmznhamOrder(orderId);
  const { imageUrl, isLoading: isImageLoading } = useCatalogItemImage(order?.items?.[0]?.itemId);

  return (
    <OrderTrackingLayout
      view={order ? toAmznhamTrackingView(order, imageUrl) : undefined}
      isLoading={isLoading || isImageLoading}
      isError={isError}
      onRetry={() => refetch()}
      notFoundLink={{
        label: getString("orderTracking.notFoundLinks.yourOrders"),
        url: Paths.orders.url,
      }}
      banners={<NotificationBanners />}
      headerAction={order && <OrderSupportActions order={order} onMessageSent={() => refetch()} />}
      summaryAction={order && <CancelAmznhamOrder order={order} />}
    />
  );
};

export default AmznhamOrderTrackingPage;
